<template>
    <div>
        <div class="table-header-buttons-container">

            <div style="padding-inline-end: 12px;">
                <disk-usage-meter :quota="quota"
                                  :usage="usage"
                                  big/>
            </div>

            <template v-if="privilegeAddMedia">

                <div class="table-header-buttons-container prepended">
                    <v-btn
                        :elevation="2"
                        type="button"
                        class="btn btn-primary"
                        @click="onAddMediaFileButtonClick">
                        <v-icon left>far fa-plus</v-icon>
                        {{ t_add_new_media_file }}
                    </v-btn>
                </div>

            </template>

            <div class="table-header-buttons-container">

                <template v-if="privilegeEditMedia">
                    <transition name="fade">
                        <v-btn v-if="someEntriesSelected"
                               :disabled="loading"
                               :elevation="2"
                                class="btn btn-secondary"
                               @click="onMultiEditButtonClick">
                            <v-icon left>far fa-pencil</v-icon>
                            {{ t_edit_selected_media_files }}
                        </v-btn>
                    </transition>
                </template>

                <template v-if="privilegeDeleteMedia">
                    <transition name="fade">
                        <v-btn v-if="someEntriesSelected"
                               :disabled="loading"
                               :elevation="2"
                               class="button-red-on-hover btn btn-secondary"
                               @click="onMultiDeleteButtonClick">
                            <v-icon left>far fa-trash</v-icon>
                            {{ t_delete_selected_media_files }}
                        </v-btn>
                    </transition>
                </template>

            </div>
        </div>

        <table-common
            :allFilterableColumnsSelected="allFilterableColumnsSelected"
            :columnDefaults="columnDefaults"
            :columnOptions="columnOptions"
            :columnsToHideInCardMode="['preview', 'actions']"
            :entries="entriesWithOptionalHidden"
            :filters="filters"
            :filtersEmpty="filtersEmpty"
            :getEditLink="getEditLink"
            :hiddenEntriesDisplayed="hiddenEntriesDisplayed"
            :loading="loading"

            :localStorageKeyPrefix="localStorageKeyPrefix"

            :noEntriesFromBackend="noEntriesFromBackend"
            :openMenuId.sync="openMenuId"

            :rowDropdownItems="[]"
            :selectedColumns.sync="selectedColumns"
            :selectedEntries="selectedEntries"

            :selectedRowIdsMap="selectedRowIdsMap"
            :style="{
                '--thumbnail-height-ul': previewSizeModel,
                'margin-block-start': '0.75rem'
            }"

            sort-field-initial="updated_at_millis"
            :sort-order-initial="-1"

            :tableElementRef="localStorageKeyPrefix"

            :textLoading="t_loading"
            :textNoEntriesFromBackend="t_no_data_available_in_table"
            :textNoEntriesFound="t_no_matching_records_found"
            @clearFilters="clearFilters"

            @refresh="loadMediaFiles"

            @rowSelect="onTableRowSelect"

            @rowUnselect="onTableRowUnselect"
            @thumbnailPreviewClick="id => showMedia(id)"

            @update:selectedEntries="onSelectedEntriesChange"
            @update:renderedEntriesMap="onRenderedEntriesUpdate"

            @update:displayedColumns="displayedColumns = $event"

            @update:openMenuAtEnd="openMenuAtEnd = $event"
            @update:cardsDisplayMode="openMenuId = null"

            @row-contextmenu="onRowContextMenu"
        >

            <!--suppress VueUnrecognizedSlot -->
            <template #default="{ rowDropdownClick }">

                <column :footer="selectedEntriesLengthString"
                        :header="selectedEntriesLengthString"
                        :headerLabel="selectedEntriesHeaderLabel"
                        bodyClass="column-padded-start"
                        filterHeaderClass="column-padded-start"
                        footerClass="column-padded-start"
                        headerClass="column-no-resize column-padded-start column-justify-center"
                        selectionMode="multiple" />

                <column :footer="columnDefaults[0].label"
                        :header="columnDefaults[0].label"
                        :showClearButton="false"
                        :showFilterMenu="false"
                        bodyClass="column-editable"
                        field="name"
                        filterHeaderClass="column-narrow"
                        sortField="name_sortable"
                        sortable
                        sortIcon="far fa-sm fa-a"
                >

                    <!--suppress VueUnrecognizedSlot -->
                    <template #filter="{filterModel,filterCallback}">
                        <filter-autocomplete v-model="filters.name.value"
                                             :items="namesWithUserEntered"
                                             :placeholder="`${t_filter} ${columnDefaults[0].label}`"
                                             @update:userEnteredItems="userEnteredItemsMap.name = $event" />
                    </template>
                    <!--suppress VueUnrecognizedSlot -->
                    <template #body="{data}">
                        <table-editable-name :data="data"
                                             :editPrivilege="false"
                                             :is-editing="cellEditValues[data.id] != undefined"
                                             :value="cellEditValues[data.id] ?? ''"
                                             @cellEditCancel="cellEditCancel(data.id)"
                                             @cellEditConfirm="cellEditConfirm(data.id)"
                                             @cellEditEnable="cellEditEnable(data)"
                                             @input="cellEditValues[data.id] = $event"
                        />
                    </template>
                </column>

                <column :footer="columnDefaults[1].label"
                        :header="columnDefaults[1].label"
                        :hidden="!displayedColumns['preview']"
                        :showClearButton="false"
                        :showFilterMenu="false"
                        bodyClass="column-preview"
                        field="preview"
                        filterHeaderClass="column-narrow">
                    <!--suppress VueUnrecognizedSlot -->
                    <template #filter>
                        <filter-select v-model="previewSizeModel"
                                       :items="previewSizeItems"
                                       :label="columnDefaults[1].label" />
                    </template>
                    <!--suppress VueUnrecognizedSlot -->
                    <template #body="{data}">
                        <advanced-thumbnail
                            @click="showMedia(data.id)"
                            :alt="data.name"
                            :aspectRatio="data.parameters.aspectRatio"
                            :backgroundType="getBackgroundTypeFromMediaType(data.mime_type)"
                            :thumb="data.last_thumb" />
                    </template>
                </column>

                <column :footer="columnDefaults[2].label"
                        :header="columnDefaults[2].label"
                        :hidden="!displayedColumns['media_type_key']"
                        :showClearButton="false"
                        :showFilterMenu="false"
                        field="media_type_key"
                        filterHeaderClass="column-narrow"
                        headerClass="sort-icon-abstract"
                        sortIcon="far fa-sm fa-a"
                        sortField="media_type_sortnum"
                        sortable>
                    <!--suppress VueUnrecognizedSlot -->
                    <template #filter="{filterModel,filterCallback}">
                        <filter-autocomplete v-model="filters.media_type_key.value"
                                             :items="mediaTypes"
                                             itemText="text"
                                             :placeholder="`${t_filter} ${columnDefaults[2].label}`"
                                             :user-entered-items-enabled="false" />
                    </template>
                    <!--suppress VueUnrecognizedSlot -->
                    <template #body="{data}">
                        {{ mediaTypeToTranslationMap[data.media_type_key] }}
                    </template>
                </column>

                <column :footer="columnDefaults[3].label"
                        :header="columnDefaults[3].label"
                        :hidden="!displayedColumns['screen_info']"
                        :showClearButton="false"
                        :showFilterMenu="false"
                        field="screen_info"
                        filterHeaderClass="column-narrow"
                        headerClass="sort-icon-numeric"
                        sortField="parameters.aspectRatio"
                        sortable
                        :sortOrder="-1"
                        sortIcon="far fa-sm fa-1"
                >
                    <!--suppress VueUnrecognizedSlot -->
<!--                    <template #filter="{filterModel,filterCallback}">
                        <filter-autocomplete v-model="filters.screen_info.value"
                                             :items="screenInfoWithUserEntered"
                                             :placeholder="`${t_filter} ${columnDefaults[3].label}`"
                                             @update:userEnteredItems="userEnteredItemsMap.screen_info = $event" />
                    </template>-->
                    <!--suppress VueUnrecognizedSlot -->
                    <template #body="{data}">
                        {{ data.screen_info }}
                    </template>
                </column>

                <column :footer="columnDefaults[4].label"
                        :header="columnDefaults[4].label"
                        :hidden="!displayedColumns['usergroups']"
                        :showClearButton="false"
                        :showFilterMenu="false"
                        field="usergroups"
                        filterHeaderClass="column-narrow">
                    <!--:filterMatchModeOptions="filterMatchModes"-->
                    <!--suppress VueUnrecognizedSlot -->
                    <template #filter="{filterModel,filterCallback}">
                        <filter-autocomplete v-model="filters.usergroups.value"
                                             :items="usergroupsWithUserEntered"
                                             :placeholder="`${t_filter} ${columnDefaults[4].label}`"
                                             super-tags
                                             @update:userEnteredItems="userEnteredItemsMap.usergroups = $event" />
                    </template>
                    <!--suppress VueUnrecognizedSlot -->
                    <template #body="{data}">
                        <div class="tags-container">
                            <table-tag v-for="usergroup in data.usergroups"
                                       :key="usergroup.id">
                                {{ usergroup.name }}
                            </table-tag>
                        </div>
                    </template>
                </column>

                <column :footer="columnDefaults[5].label"
                        :header="columnDefaults[5].label"
                        :hidden="!displayedColumns['tags']"
                        :showClearButton="false"
                        :showFilterMenu="false"
                        field="tags"
                        filterHeaderClass="column-narrow" >
                    <!--suppress VueUnrecognizedSlot -->
                    <template #filter="{filterModel,filterCallback}">
                        <filter-autocomplete v-model="filters.tags.value"
                                             :items="tagsWithUserEntered"
                                             :placeholder="`${t_filter} ${columnDefaults[5].label}`"
                                             super-tags
                                             @update:userEnteredItems="userEnteredItemsMap.tags = $event" />
                    </template>
                    <!--suppress VueUnrecognizedSlot -->
                    <template #body="{data}">
                        <div class="tags-container">
                            <table-super-tag v-for="tag in data.tags"
                                             :key="tag.id"
                                             :value="tag.name" />
                        </div>
                    </template>
                </column>

                <column :footer="columnDefaults[6].label"
                        :header="columnDefaults[6].label"
                        :hidden="!displayedColumns['validity_text']"
                        :showClearButton="false"
                        :showFilterMenu="false"
                        bodyClass="text-center"
                        field="validity_text"
                        filterField="validity_text"
                        filterHeaderClass="column-narrow"
                        sortField="valid_from_millis"
                        sortable
                        :sortOrder="-1"
                        sortIcon="far fa-calendar-day"
                >
                    <!--suppress VueUnrecognizedSlot -->
<!--                    <template #filter="{filterModel,filterCallback}">
                        <filter-autocomplete v-model="filters.validity_text.value"
                                             :items="validityWithUserEntered"
                                             :placeholder="`${t_filter} ${columnDefaults[6].label}`"
                                             @update:userEnteredItems="userEnteredItemsMap.validity_text = $event" />
                    </template>-->
                    <!--suppress VueUnrecognizedSlot -->
                    <template #body="{data}">
                        <div :class="data.parameters['validity-status']">
                            {{ data.validity_text }}
                        </div>
                    </template>
                </column>

                <column :footer="columnDefaults[7].label"
                        :header="columnDefaults[7].label"
                        :hidden="!displayedColumns['size']"
                        :showClearButton="false"
                        :showFilterMenu="false"
                        field="media_size_string"
                        filterHeaderClass="column-narrow"
                        sortField="media_size"
                        sortable
                        :sortOrder="-1"
                        sortIcon="far fa-sm fa-1"
                >
                    <!--suppress VueUnrecognizedSlot -->
<!--                    <template #filter="{filterModel,filterCallback}">
                        <filter-autocomplete v-model="filters.size.value"
                                             :items="sizeWithUserEntered"
                                             :placeholder="`${t_filter} ${columnDefaults[7].label}`"
                                             @update:userEnteredItems="userEnteredItemsMap.size = $event" />
                    </template>-->
                </column>

                <column :footer="columnDefaults[8].label"
                        :header="columnDefaults[8].label"
                        :hidden="!displayedColumns['updated_at']"
                        :showClearButton="false"
                        :showFilterMenu="false"
                        field="updated_at_local"
                        filterHeaderClass="column-narrow"
                        sortField="updated_at_millis"
                        sortable
                        :sortOrder="-1"
                        sortIcon="far fa-clock"
                >

                    <!--suppress VueUnrecognizedSlot -->
<!--                    <template #filter="{filterModel,filterCallback}">
                        <filter-autocomplete v-model="filters.updated_at.value"
                                             :items="updatedAtWithUserEntered"
                                             :placeholder="`${t_filter} ${columnDefaults[8].label}`"
                                             @update:userEnteredItems="userEnteredItemsMap.updated_at = $event" />
                    </template>-->
                    <!--suppress VueUnrecognizedSlot -->
<!--                    <template #body="{data}">-->
<!--                        {{ data.updated_at }}-->
<!--                    </template>-->
                </column>

                <column :footer="columnDefaults[9].label"
                        :header="columnDefaults[9].label"
                        :hidden="!displayedColumns['created_at']"
                        :showClearButton="false"
                        :showFilterMenu="false"
                        field="created_at_local"
                        filterHeaderClass="column-narrow"
                        sortField="created_at_millis"
                        sortable
                        :sortOrder="-1"
                        sortIcon="far fa-clock"
                >

                    <!--suppress VueUnrecognizedSlot -->
<!--                    <template #filter="{filterModel,filterCallback}">
                        <filter-autocomplete v-model="filters.created_at.value"
                                             :items="createdAtWithUserEntered"
                                             :placeholder="`${t_filter} ${columnDefaults[9].label}`"
                                             @update:userEnteredItems="userEnteredItemsMap.created_at = $event" />
                    </template>-->
                </column>

                <column :footer="columnDefaults[10].label"
                        :header="columnDefaults[10].label"
                        :showClearButton="false"
                        :showFilterMenu="false"
                        :styles="{ 'line-height':'unset'}"
                        bodyClass="column-padded-end column-frozen-right"
                        filterHeaderClass="column-padded-end column-frozen-right no-min-width"
                        footerClass="column-padded-end column-frozen-right"
                        headerClass="column-padded-end column-frozen-right">
                    <!--suppress VueUnrecognizedSlot -->
                    <template #filter>
                        <square-button
                            :buttonClasses="[{ 'filter-clear-button-invisible': filtersEmpty }, 'fade-enter-active']"
                            :tooltip-text="t_clear_filters"
                            flat
                            icon="fas fa-filter-slash"
                            @click="clearFilters" />
                    </template>
                    <template #body="{data}">
                        <div :id="`btnSet${data.id}`"
                             :class="{'table-action-button-menu-open': openMenuId === data.id}"
                             class="table-action-button"
                             style="white-space: nowrap; display: inline-flex; width: fit-content;  flex-direction: row;"
                        >
                            <template v-for="action in rowActions">
                                <div class="mx-1">
                                    <square-button
                                       flat
                                       :tooltipText="action.label"
                                       :icon="action.icon"
                                       @click="action.command(data.id)"
                                    />
                                </div>
                            </template>

                            <list-dropdown-menu
                                :id="data.id"
                                :attach="`#rowDropMenu${data.id}`"
                                :items="rowActions"
                                :value="openMenuId === data.id"
                                rounded
                            />

                            <portal>
                                <div :id="`rowDropMenu${data.id}`" class="popper-menu-anchor popper-menu-hide-under-navbar"/>
                            </portal>
                        </div>
                    </template>
                </column>
            </template>
        </table-common>
    </div>
</template>

<script>
import { defineComponent } from 'vue'
import { getBackgroundTypeFromMediaType } from '../../../utility'
import MediapoolService from '../../../services/MediapoolService'
import entriesCommonMixin from '../../../mixins/entriesCommonMixin';
import entriesLoaderMixin from '../../../mixins/entriesLoaderMixin';
import tableMixin from '../../../mixins/tableMixin'
import popperContextMenuMixin from '../../../mixins/popperContextMenuMixin';

import TableCommon from '../common/table/table-common.vue'
import TableTag from '../common/table/table-tag.vue'
import TableSuperTag from '../common/table/table-super-tag.vue'
import FilterAutocomplete from '../common/table/filter-autocomplete.vue'
import Column from '../common/PrimeVue/Column.vue'
import ListDropdownMenu from '../common/list-dropdown-menu.vue'
import FilterSelect from '../common/table/filter-select.vue'
import TableEditableName from '../common/table/table-editable-name.vue'
import AdvancedThumbnail from '../common/advanced-thumbnail.vue'
import DiskUsageMeter from './disk-usage-meter.vue'
import toaster from '../../common/toaster'

import { Portal } from '@linusborg/vue-simple-portal'
import SquareButton from '../common/squareButton.vue'

import '../../../../prototypeExtensions/String'
import { mediaTypeToSortOrderMap, mediaTypeToTranslationMap } from '../../../../utils/types';

export default defineComponent({
    components: {
        SquareButton,
        DiskUsageMeter,
        AdvancedThumbnail,
        TableEditableName,
        FilterSelect,
        ListDropdownMenu,
        TableCommon,
        TableTag,
        TableSuperTag,
        FilterAutocomplete,
        Column,
        Portal
    },
    mixins: [
        entriesCommonMixin,
        entriesLoaderMixin,
        tableMixin,
        popperContextMenuMixin,
    ],
    props: {
        files: undefined,
        /** @type {'1' | undefined} */
        privilegeViewMedia: String,
        /** @type {'1' | undefined} */
        privilegeAddMedia: String,
        /** @type {'1' | undefined} */
        privilegeDeleteMedia: String,
        /** @type {'1' | undefined} */
        privilegeEditMedia: String
    },
    data() {
        return {
            mediapoolService: new MediapoolService(),

            editPrivilege: false,
            quota: undefined,
            usage: undefined,

            /** @type {{label: string, value: string, disabled?: boolean}[]} */
            columnDefaults: [
                {
                    label: Lang.langGet('Name').capitalize(),
                    value: 'name',
                    disabled: true
                },
                {
                    label: Lang.langGet('Preview').capitalize(),
                    value: 'preview'
                },
                {
                    label: Lang.langGet('Type').capitalize(),
                    value: 'media_type_key'
                },
                {
                    label: Lang.langGet('Resolution').capitalize(),
                    value: 'screen_info'
                },
                {
                    label: Lang.langGet('Usergroups').capitalize(),
                    value: 'usergroups'
                },
                {
                    label: Lang.langGet('Tags').capitalize(),
                    value: 'tags'
                },
                {
                    label: Lang.langGet('Validity').capitalize(),
                    value: 'validity_text'
                },
                {
                    label: Lang.langGet('Size').capitalize(),
                    value: 'size'
                },
                {
                    label: Lang.langGet('Last modified').capitalize(),
                    value: 'updated_at'
                },
                {
                    label: Lang.langGet('Created at').capitalize(),
                    value: 'created_at'
                },
                {
                    label: Lang.langChoice('Action|Actions', 4).capitalize(),
                    value: 'actions',
                    disabled: true
                }
            ],

            /** @type {Record.<string, {matchMode: string, value: string[], num?: true, fieldName?: string}>} */
            filters: {
                name: {
                    matchMode: 'containsArray_OR',
                    value: []
                },
                media_type_key: {
                    // Must be left at OR, because they will never overlap
                    // (an element will not be both 'picture' and 'video')
                    // and users will expect them to always act with OR filtering (any must match).
                    matchMode: 'containsArray_OR',
                    value: []
                },
                usergroups: {
                    matchMode: 'arrayContainsNameArray_OR',
                    value: []
                },
                tags: {
                    matchMode: 'arrayContainsNameArray_OR',
                    value: []
                },
            },

            userEnteredItemsMap: {
                name: [],
                usergroups: [],
                tags: [],
            },

            rowActions: [],

            localStorageKeyPrefix: 'dt-mediapool',

            t_add_new_media_file: Lang.langGet('Add new media file'),
            t_delete_selected_media_files: Lang.langGet('Delete Selected Media Files'),
            t_edit_selected_media_files: Lang.langGet('Edit Selected Media Files'),
            t_export_selected_media_files: Lang.langGet('Export Selected Media Files'),

            t_failed_to_modify_template: Lang.langGet('Failed to modify template in database.'),
            t_failed_to_edit_template: Lang.langGet('Failed to edit template:'),
            t_failed_to_export_template: Lang.langGet('Failed to export template!'),
            t_failed_to_copy_template: Lang.langGet('Failed to copy template!'),

            t_template_was_copied_successfully: Lang.langGet('Template was copied successfully.'),
            t_template_was_exported_successfully: Lang.langGet('Template was exported successfully.'),

            /** Used by entriesLoaderMixin.js */
            t_data_load_error: Lang.langGet("Failed to load media files!"),
        }
    },
    created() {

        if (this.privilegeEditMedia) {
            this.rowActions.push({
                label: this.t_edit,
                icon: 'far fa-pencil',
                command: (id) => {
                    EaseEventBus.fire('editMedia', this.entriesMap[id])
                }
            })
        }

        if (this.privilegeDeleteMedia) {
            this.rowActions.push({
                label: this.t_delete,
                icon: 'far fa-trash',
                class: 'menu-button-delete',
                command: (id) => {
                    EaseEventBus.fire('deleteMedia', this.entriesMap[id])
                }
            })
        }

        this.loadMediaFiles()

        EaseEventBus.listen('onMediaFilesDeleted', (ids) => {
            const idArray = Array.isArray(ids) ? ids : [ids]
            idArray.forEach(id => {
                delete this.entriesMap[id]
            })
            this.entriesMap = { ...this.entriesMap }
            this.refreshLocalMediaFiles()
        })

        EaseEventBus.listen('reloadData', this.loadMediaFiles)
        EaseEventBus.listen('updateMedialist', this.loadMediaFiles)
    },
    methods: {
        getBackgroundTypeFromMediaType,
        async loadMediaFiles() {
            const dataResponse = await this.loadData(this.mediapoolService.getMediaFiles, this.fieldsToKeep)

            this.quota = dataResponse.hdd_quota
            this.usage = dataResponse.hdd_usage
        },
        refreshLocalMediaFiles() {
            this.loadData(this.entries, this.fieldsToKeep)
        },
        getEditLink(data) {
            return '/storage/mediapool/' + Laravel.activeProject + '/' + data.name + '.' + data.file_extension;
        },
        showMedia(id) {
            const media_file = this.files.find((media_file) => media_file.id === id);
            if (this.files.file_name === 'dummy_placeholder') {
                EaseEventBus.fire('newContent', media_file.media_type);
            } else {
                EaseEventBus.fire('showMedia', media_file);
            }
        },
        onAddMediaFileButtonClick() {
            EaseEventBus.fire('showAddMediaModal')
        },
        onImportButtonClick() {
            EaseEventBus.fire('showModal', 'content-import')
        },
        onExportButtonClick() {
            this.selectedEntries.forEach((selectedEntry) => {
                this.exportMediaFile(selectedEntry.id)
            })
        },
        onMultiEditButtonClick() {
            EaseEventBus.fire('editMedia', this.selectedEntries.length > 1 ? this.selectedEntries : this.selectedEntries[0])
        },
        onMultiDeleteButtonClick() {
            EaseEventBus.fire('deleteMedia', this.selectedEntries)
        },
        exportMediaFile(templateId) {
            axios.post('/content/template/export', {
                id: templateId,
                project: Laravel.activeProject
            }).then(({ data, status }) => {

                if (status === 401) {
                    toastr.error(this.t_failed_to_export_template)
                    window.location = '/login'
                    return
                }

                toastr.success(this.t_template_was_exported_successfully)
                const link = document.createElement('a')
                link.href = data
                link.download = data.substr(data.lastIndexOf('/') + 1)
                link.click()
                link.remove()

            }).catch((error) => {
                toastr.error(`${this.t_failed_to_export_template} ${error}`)
            })
        },
        cellEditConfirm(mediaId) {
            // Disabled for mediapool due to a missing 'update-name' endpoint
            /*
            if (!this.editPrivilege) return

            const mediaName = this.cellEditValues[mediaId];
            if(!mediaName) return;

            // This endpoint does not exist at this point
            axios.post(`/content/mediapool/edit/${mediaId}/update-name`, {
                project: Laravel.activeProject,
                id: mediaId,
                name: mediaName
            }).then(() => {
                this.rowNameUpdate({ id: mediaId, name: mediaName })
            }).catch((error) => {
                if (error.response.status === 401) {
                    window.location = '/login'
                } else if (error.response.status === 422) {
                    let errorString = this.t_failed_to_edit_template + ' '
                    for (let key in error.response.data) {
                        errorString = errorString + error.response.data[key][0] + ' '
                    }
                    toaster.error(errorString)
                } else {
                    toaster.error(this.t_failed_to_modify_template)
                }
            }).finally(() => this.cellEditCancel(mediaId))
            */
        }
    },
    computed: {
        mediaTypeToTranslationMap() {
            return mediaTypeToTranslationMap
        },
        fieldsToKeep() {
            const fieldsToKeep = this.columnDefaults.map(columnDefault => columnDefault.value)
            delete fieldsToKeep['actions']
            fieldsToKeep.push(
                'id',
                'parameters',
                'last_thumb',
                'name_sortable',
                'updated_at_millis',
                'created_at_millis',
                'validity_text',
                'valid_from_millis',
                'media_size_string',
                'media_size',
                'mime_type',
                'media_type_key',
                'media_type_sortnum',
                'file_name',
                'file_extension',
                'layers',
                'updated_at_local',
                'created_at_local',
            )
            return fieldsToKeep
        },
        namesWithUserEntered() {
            return this.filteredItemsWithUserEntered(this.fieldValuesMap.name, this.userEnteredItemsMap.name);
        },
        mediaTypes() {
            let returnValue = [];

            /* Push mediaType items into returnValue according to the predefined order of mediaTypes.
             * Add translated text onto these items. Luckily the v-autocomplete component handles this
             * and shows translated text of these "sortOrder" items in the dropdown, even though the
             * actual items concatenated below do not have the 'text' translated property. */
            for (const mediaType in mediaTypeToSortOrderMap) {
                if (this.fieldValuesMap.media_type_key?.some(mediaTypeKeyEntry => mediaTypeKeyEntry.value == mediaType)) {
                    returnValue.push({ value: mediaType, text: mediaTypeToTranslationMap[mediaType] });
                }
            }

            const mediaTypeItems = this.filteredItemsWithUserEntered(this.fieldValuesMap.media_type_key, null, true);

            if (mediaTypeItems) {
                returnValue = returnValue.concat(mediaTypeItems);
            }

            return returnValue;
        },
        usergroupsWithUserEntered() {
            return this.filteredItemsWithUserEntered(this.fieldValuesMap.usergroups, this.userEnteredItemsMap.usergroups);
        },
        tagsWithUserEntered() {
            let returnValue = [{
                value: '§hidden',
                disabled: !this.anyEntriesHidden
            }, {
                value: '§imported',
                disabled: !this.anyEntriesImported
            }];
            const tagItems = this.filteredItemsWithUserEntered(this.fieldValuesMap.tags, this.userEnteredItemsMap.tags);

            if (tagItems) {
                returnValue = returnValue.concat(tagItems)
            }
            return returnValue
        },
    },
    mounted() {
        setTimeout(() => {
            document.querySelector('input.dz-hidden-input').remove()
        }, 50)
    }
})
</script>

<style>
body {
    /* Disable body min-width on this page, since it's responsive */
    min-width: unset !important;
}
</style>

